import IconButton from "@mui/material/IconButton";
import "assets/css/event.css";
import dltImg from "assets/svg/delete.svg";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import ActionCol from "components/events/EventList/ActionCol";
import ConditionCol from "components/events/EventList/ConditionCol";
import DefaultModal from "components/modals/ModalTemplate";
import Pagination from "components/shared/Pagination";
import {
    EVENT_ENABLED_DELETE_MESSAGE,
    FAILED_ACTION_MESSAGE,
} from "constant/index";
import { Registry } from "generated/models";
import { Event } from "types/Event"
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { useDisableGatewayEventConfirm } from "hooks/useDisableGatewayEventConfirm";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { get } from "lodash";
import { NewEventModal } from "components/events/modals/NewEventModal";
import { PaginationType } from "pages/events/Events";
import { KeyboardEvent, useState } from "react";
import {
    Button,
    Col,
    Form,
    FormControl,
    InputGroup,
    Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import EventService from "service/eventService";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { isValid, showWarning } from "utils/eventFunctions";
import { isHttpSuccess } from "utils/functions";

type EventListProps = {
    eventList: Event[];
    allGateways: Registry[];
    fetchEventList: Function;
    setEventList: Function;
    eventSearchText: string;
    setEventSearchText: Function;
    pagination: PaginationType;
    setPagination: Function;
    sortIcon: Function;
};

const EventList = ({
    eventList,
    allGateways,
    fetchEventList,
    setEventList,
    eventSearchText,
    setEventSearchText,
    pagination,
    setPagination,
    sortIcon,
}: EventListProps) => {
    const [smShow, setSmShow] = useState(false);
    const handleClose = () => setSmShow(false);

    const [toDelete, setToDelete] = useState("");

    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState("");
    const [modalContent, setModalContent] = useState("");
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const { confirmIfNeeded, disableConfirmModal } =
        useDisableGatewayEventConfirm();

    const deleteEvent = async (eventId: string) => {
        const response = await EventService.deleteEvent(eventId);
        if (isHttpSuccess(response.status)) {
            fetchEventList();
            showSuccessAlert({
                message: `Event has been deleted successfully.`,
            });
            setModalShow(false);
        } else {
            showErrorAlert(getAPIError(response, FAILED_ACTION_MESSAGE));
        }
    };

    const performToggleEventStatus = async (event: Event) => {
        const { uuid: event_id, activate: active } = event;
        const response = await EventService.updateEvent(event_id, {
            activate: !active,
        });
        if (isHttpSuccess(response.status)) {
            const updatedEventList = eventList.map((e) => {
                if (event_id === e.uuid) {
                    e.activate = !e.activate;
                }
                return e;
            });
            setEventList(updatedEventList);

            showSuccessAlert({
                message: `Event has been ${active ? "disabled" : "enabled"
                    } successfully.`,
            });
        } else {
            showErrorAlert(getAPIError(response, FAILED_ACTION_MESSAGE));
        }
    };

    const toggleEventStatus = (event: Event) => {
        // check the device of condition is valid
        if (!isValid(event, allGateways)) {
            return showWarning();
        }

        confirmIfNeeded(event, () => performToggleEventStatus(event));
    };

    const showDeleteModal = (event: Event) => {
        if (event.activate) {
            return showErrorAlert({
                message: EVENT_ENABLED_DELETE_MESSAGE,
            });
        }

        setModalShow(true);
        setModalType("dlt");
        setModalContent(`Do you want to delete this event?`);
    };

    const renderEventList = () => {
        if (eventSearchText && !eventList.length) {
            return <div className="mt-5 text-center">No event found</div>;
        } else if (eventList.length) {
            return renderEvents();
        } else {
            return (
                <div className="mt-5 text-center">No Events to display.</div>
            );
        }
    };

    const renderEvents = () => {
        let events = eventList.map((event) => {
            return (
                <div className="table-row" key={event.uuid}>
                    <Row className="no-checkbox">
                        <Col md={{ offset: 0 }} xs={{ span: 11, order: 1 }}>
                            <Link
                                to={{
                                    pathname: `/event-details/${event.uuid}`,
                                }}
                            >
                                {event.name}
                            </Link>
                        </Col>
                        <Col
                            md={{ offset: 0, order: 3 }}
                            xs={{ span: 12, order: 3 }}
                        >
                            {event.gateway_id ? "Gateway based" : "Global"}
                        </Col>
                        <Col
                            md={{ offset: 0, order: 3 }}
                            xs={{ span: 12, order: 3 }}
                        >
                            <ConditionCol
                                conditions={get(event, "conditions", [])}
                            />
                        </Col>
                        <Col
                            className="event-action-col"
                            md={{ offset: 0, order: 3 }}
                            xs={{ span: 12, order: 3 }}
                        >
                            <ActionCol actions={get(event, "actions", [])} />
                        </Col>
                        <Col
                            md={{ span: 1, offset: 0 }}
                            xs={{ span: 2, order: 4 }}
                        >
                            <HoverAuthorizeTooltip permission="event:update">
                                <span className="d-inline-block">
                                    <Form.Check
                                        type="switch"
                                        id={`switch_${event.uuid}`}
                                        label=""
                                        checked={event.activate}
                                        onChange={() =>
                                            toggleEventStatus(event)
                                        }
                                    />
                                </span>
                            </HoverAuthorizeTooltip>
                        </Col>
                        <Col
                            md={{ offset: 0, span: 1 }}
                            xs={{ offset: 8, span: 2, order: 5 }}
                        >
                            <HoverAuthorizeTooltip permission="event:delete">
                                <IconButton
                                    className="btn"
                                    aria-label="delete-event"
                                    onClick={() => {
                                        showDeleteModal(event);
                                        setToDelete(event.uuid);
                                    }}
                                    onKeyDown={() => {
                                        showDeleteModal(event);
                                        setToDelete(event.uuid);
                                    }}
                                >
                                    <img src={dltImg} alt="delete" />
                                </IconButton>
                            </HoverAuthorizeTooltip>
                        </Col>
                        <Col
                            md={{ span: 1, order: "last" }}
                            xs={{ span: 1, order: 2 }}
                        >
                            <Link
                                to={{
                                    pathname: `/event-details/${event.uuid}`,
                                }}
                            >
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Link>
                        </Col>
                    </Row>
                </div>
            );
        });
        return events;
    };

    return (
        <>
            <Row>
                <Col
                    sm={12}
                    className="d-md-flex justify-content-end table-head-options"
                >
                    <HoverAuthorizeTooltip
                        permission="event:create"
                        className="mr-3 mb-2"
                    >
                        <Button
                            className="btn btn-primary"
                            onClick={() => {
                                walletModalTrigger(() => setSmShow(true), true);
                            }}
                        >
                            + New Event
                        </Button>
                    </HoverAuthorizeTooltip>
                    <div className="search">
                        <InputGroup>
                            <FormControl
                                type="text"
                                placeholder="Search for.."
                                onChange={(e) => {
                                    setEventSearchText(e.target.value);
                                }}
                                aria-describedby="button-addon2"
                                onKeyPress={(
                                    e: KeyboardEvent<HTMLInputElement>
                                ) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        fetchEventList();
                                    }
                                }}
                            />
                        </InputGroup>
                    </div>
                    <Button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                            fetchEventList();
                        }}
                    >
                        Search
                    </Button>
                </Col>
            </Row>
            <Row className="cstm-table events-list mt-4">
                <Col sm={12}>
                    {!IS_BASIC_PLAN && (
                        <WalletAlertComponent showWarningWhen="LowBalance" />
                    )}
                </Col>
                <Col sm={12}>
                    <div className="table-head">
                        <Row className="no-checkbox">
                            <Col>Event Name {sortIcon("name")}</Col>
                            <Col>Event Type {sortIcon("gateway_id")}</Col>
                            <Col>Event Conditions {sortIcon("conditions")}</Col>
                            <Col>Event Actions {sortIcon("actions")}</Col>
                            <Col md={{ span: 1 }}>
                                Status {sortIcon("activate")}
                            </Col>
                            <Col md={{ span: 1 }}>Action</Col>
                            <Col md={{ span: 1 }}></Col>
                        </Row>
                    </div>
                    {renderEventList()}
                </Col>
            </Row>
            {pagination.count > 0 && (
                <Pagination
                    pagination={pagination}
                    setPagination={setPagination}
                />
            )}
            <NewEventModal show={smShow} onClose={handleClose} />

            <DefaultModal
                modalShow={modalShow}
                setModalShow={setModalShow}
                modalType={modalType}
                modalContent={modalContent}
                deleteFunction={() => {
                    deleteEvent(toDelete);
                }}
            />

            {disableConfirmModal}
        </>
    );
};

export default EventList;
